/**
 * Legend transform.
 *
 * Controls legend visibility, position, and orientation.
 * Grid/spacing adjustments are handled by layout.ts.
 */
import type { EChartsOption } from 'echarts';
import type { ChartStyleState } from '../../../../edit/visualConfig';
import { getCapabilities } from '../../../../edit/visualConfig';
import { isSingleObject } from './helpers';

/** Legend position → ECharts layout mapping. */
const LEGEND_POSITIONS: Record<string, Record<string, unknown>> = {
  top: { top: 4, left: 'center', orient: 'horizontal', padding: [8, 32, 8, 32] },
  bottom: { bottom: 8, left: 'center', orient: 'horizontal', padding: [8, 32, 8, 32] },
  left: { left: 16, top: 'middle', orient: 'vertical', padding: [140, 8, 16, 0] },
  right: { right: 16, top: 'middle', orient: 'vertical', padding: [140, 0, 16, 8] },
};

export function applyLegend(option: EChartsOption, vc: ChartStyleState, chartType?: string): EChartsOption {
  const result = { ...option };
  if (!result.legend) return result;

  const leg: any = Array.isArray(result.legend) ? result.legend[0] : result.legend;
  if (!isSingleObject(leg)) return result;

  // 'auto' → show when >1 series, or always for non-axis charts (pie/funnel)
  // whose legend identifies data items rather than series.
  const seriesCount = Array.isArray(result.series) ? result.series.length : 0;
  const caps = getCapabilities((chartType ?? 'bar') as any);
  const show = vc.showLegend === 'auto'
    ? (seriesCount > 1 || !caps.axis)
    : vc.showLegend;
  leg.show = show;

  if (!show) return result;

  // Apply position & orientation
  const pos = LEGEND_POSITIONS[vc.legendPosition] ?? LEGEND_POSITIONS.top;
  delete leg.top; delete leg.bottom; delete leg.left; delete leg.right; delete leg.orient;
  Object.assign(leg, pos);

  return result;
}